import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { ArrowLeft, ExternalLink, SearchX } from 'lucide-react';

import { CopyButton } from '@/components/admin/copy-button';
import { EmptyState } from '@/components/ui/empty-state';
import { useTranslations } from '@/i18n/use-locale';
import { previewKind } from '@/lib/detail';
import { cn } from '@/lib/utils';
import { Card } from '@/components/ui/card';

/**
 * The pieces every detail screen is built from — the back link, a titled section, a
 * label/value pair, a copyable contact, a counter tile, the not-found state and the
 * embedded document preview. One set, so the talent, organization and review screens
 * read as the same console.
 */

/** Back to the list, pointing toward it: left in English, right in Arabic. */
export function BackLink({ to, label }: { to: string; label: string }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-conc-500 transition hover:text-navy"
    >
      <ArrowLeft aria-hidden className="h-4 w-4 rtl:rotate-180" />
      {label}
    </Link>
  );
}

export function DetailSection({
  title,
  children,
  className,
  actions,
}: {
  title: string;
  children: ReactNode;
  className?: string;
  actions?: ReactNode;
}) {
  return (
    <Card asChild>
      <section aria-label={title} className={cn('p-5 shadow-sm', className)}>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-[14px] font-bold text-navy">{title}</h3>
          {actions}
        </div>
        {children}
      </section>
    </Card>
  );
}

/** A `<dl>` grid; put `<Field>`s in it. */
export function FieldGrid({ children, className }: { children: ReactNode; className?: string }) {
  return <dl className={cn('grid gap-3 text-[12.5px] sm:grid-cols-2', className)}>{children}</dl>;
}

/** One label and its value. A missing value shows a dash rather than an empty row. */
export function Field({
  label,
  children,
  className,
}: {
  label: string;
  children?: ReactNode;
  className?: string;
}) {
  const empty = children === undefined || children === null || children === '';
  return (
    <div className={className}>
      <dt className="text-conc-500">{label}</dt>
      <dd className={cn('font-semibold text-navy', empty && 'font-normal text-conc-400')}>
        {empty ? '—' : children}
      </dd>
    </div>
  );
}

/** A phone or an email: kept left-to-right inside the RTL page, with a copy control. */
export function ContactValue({
  value,
  copyLabel,
  numeric = false,
}: {
  value: string;
  copyLabel: string;
  numeric?: boolean;
}) {
  const common = useTranslations('common');
  return (
    <span className="flex items-center gap-1">
      <span className={cn('ltr-field truncate', numeric && 'num')}>{value}</span>
      <CopyButton value={value} label={copyLabel} copiedLabel={common('copied')} />
    </span>
  );
}

export function StatTile({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="rounded-xl bg-sideline p-3 text-center">
      <p className="num text-[18px] font-bold text-navy">{value}</p>
      <p className="text-[11.5px] text-conc-500">{label}</p>
    </div>
  );
}

/** The record is not there — deleted, decided and gone from the queue, or a bad link. */
export function DetailNotFound({
  title,
  hint,
  backTo,
  backLabel,
}: {
  title: string;
  hint: string;
  backTo: string;
  backLabel: string;
}) {
  return (
    <EmptyState
      icon={SearchX}
      title={title}
      hint={hint}
      action={
        <Link
          to={backTo}
          className="rounded-lg bg-pitch px-4 py-2 text-[13px] font-bold text-white transition hover:bg-pitch-800"
        >
          {backLabel}
        </Link>
      }
    />
  );
}

export interface DetailNotFoundProps {
  title: string;
  hint: string;
  backTo: string;
  backLabel: string;
}

/** The whole screen for a record that is not there: the back link, then the not-found state. */
export function DetailNotFoundPage(props: DetailNotFoundProps) {
  return (
    <div className="space-y-6">
      <BackLink to={props.backTo} label={props.backLabel} />
      <DetailNotFound {...props} />
    </div>
  );
}

/**
 * A document under review, embedded: an image inline, anything else in a frame. The
 * open-in-a-new-tab link is always offered, because a signed PDF in a frame is at the
 * browser's mercy and a full tab is how an admin reads the small print.
 */
export function DocumentPreview({ url, title }: { url: string; title: string }) {
  const t = useTranslations('admin.detail');
  const kind = previewKind(url);

  return (
    <div className="space-y-2">
      <div className="overflow-hidden rounded-xl border border-conc-100 bg-sideline">
        {kind === 'image' ? (
          <img src={url} alt={title} className="mx-auto max-h-[560px] w-auto object-contain" />
        ) : (
          <iframe src={url} title={title} className="h-[560px] w-full bg-white" />
        )}
      </div>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 text-[13px] font-semibold text-pitch-700 hover:underline"
      >
        <ExternalLink aria-hidden className="h-4 w-4" />
        {t('openInNewTab')}
      </a>
    </div>
  );
}
